// DayView — single-day calendar view.
//
// A simpler sibling of WeekView. Renders ONE day at a time with the
// same 06:00-22:00 time grid, snap-to-15min slots, click-to-add, and
// the live "now" indicator + "Jump to now" pill. We intentionally
// don't add drag-to-resize here — the Day View is for quick "what's
// happening today" glances and high-density mobile use; users who
// want gesture editing already have Week + Month with that feature.
//
// Privacy indicator: every chip carries the same EyeOff badge that
// the other views use for `!is_shared` events, so visibility status
// is obvious at a glance.

import { useMemo, useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Repeat, Eye, EyeOff, Clock, BookOpen } from "lucide-react";

const HOURS = Array.from({ length: 17 }, (_, i) => 6 + i); // 06..22
const WINDOW_START_MIN = 6 * 60;
const WINDOW_END_MIN = 22 * 60;
const TOTAL_MIN = WINDOW_END_MIN - WINDOW_START_MIN; // 960 min
const SNAP_MIN = 15;

const TYPE_LABEL = {
  custody: "Custody",
  pickup: "Pick-up",
  dropoff: "Drop-off",
  school: "School",
  extracurricular: "Extracurricular",
  sports: "Sports",
  holiday: "Holiday",
  call: "Phone call",
  video_call: "Video call",
  other: "Other",
};

function minSinceMidnight(d) {
  return d.getHours() * 60 + d.getMinutes();
}

function fmtHour(h) {
  return `${String(h).padStart(2, "0")}:00`;
}

function isSameLocalDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Props mirror WeekView but reduced to a single day.
 *
 * @param {Date}   anchor          The single date to render.
 * @param {array}  events          Already-filtered events that COULD fall
 *                                 inside this day (parent filters child
 *                                 by `visibleEvents`).
 * @param {fn}     onPrev/onNext   Day-stepper.
 * @param {fn}     onToday         Reset to today.
 * @param {fn}     onEventClick    Open edit / day-detail.
 * @param {fn}     onSlotClick     Click an empty slot to open
 *                                 NewEventDialog pre-filled at that time.
 * @param {fn}     typeClass       Same colour helper as the other views.
 * @param {fn}     parentIdx       Resolve parent index for custody colours.
 */
export function DayView({
  anchor,
  events,
  onPrev,
  onNext,
  onToday,
  onEventClick,
  onSlotClick,
  typeClass,
  parentIdx,
  isMediator,
}) {
  // Live "now" tracking — same pattern as WeekView v77.99.28.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(t);
  }, []);

  const nowLineRef = useRef(null);

  const isToday = useMemo(() => isSameLocalDay(anchor, new Date()), [anchor]);
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const isNowInWindow = nowMin >= WINDOW_START_MIN && nowMin <= WINDOW_END_MIN;
  const showNowPill = isToday && isNowInWindow;
  const nowTopPct = isNowInWindow
    ? ((nowMin - WINDOW_START_MIN) / TOTAL_MIN) * 100
    : null;

  const handleJumpToNow = () => {
    nowLineRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const nowLabel = useMemo(() => {
    const hh = String(now.getHours()).padStart(2, "0");
    const mm = String(now.getMinutes()).padStart(2, "0");
    return `${hh}:${mm}`;
  }, [now]);

  const dayLabel = useMemo(
    () =>
      anchor.toLocaleDateString(undefined, {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      }),
    [anchor]
  );

  // Events that actually fall on this date and overlap the 06:00-22:00
  // window. Out-of-window events still appear in the chronological "All
  // day / out of hours" rail above the grid so they don't get lost.
  const { inWindow, outOfWindow } = useMemo(() => {
    const within = [];
    const outside = [];
    for (const e of events) {
      if (!e.start) continue;
      const s = new Date(e.start);
      if (!isSameLocalDay(s, anchor)) continue;
      const sMin = minSinceMidnight(s);
      if (sMin >= WINDOW_START_MIN && sMin <= WINDOW_END_MIN) {
        within.push({ ...e, _startMin: sMin });
      } else {
        outside.push({ ...e, _startMin: sMin });
      }
    }
    within.sort((a, b) => a._startMin - b._startMin);
    outside.sort((a, b) => a._startMin - b._startMin);
    return { inWindow: within, outOfWindow: outside };
  }, [events, anchor]);

  // Slot click handler — snap to the nearest 15-min slot.
  const onGridClick = (e) => {
    if (isMediator) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const pct = Math.max(0, Math.min(1, y / rect.height));
    const totalMin = WINDOW_START_MIN + Math.round((pct * TOTAL_MIN) / SNAP_MIN) * SNAP_MIN;
    const slotDate = new Date(
      anchor.getFullYear(),
      anchor.getMonth(),
      anchor.getDate(),
      Math.floor(totalMin / 60),
      totalMin % 60,
      0,
      0
    );
    onSlotClick?.(slotDate);
  };

  return (
    <div
      className="border border-[#D9E1E3] bg-white rounded-md overflow-hidden min-w-0 w-full"
      data-testid="day-grid"
    >
      {/* Header row: day stepper + label + Today + Jump-to-now pill. */}
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-[#D9E1E3] bg-[#F5F7F8]">
        <Button
          size="sm"
          variant="ghost"
          onClick={onPrev}
          className="h-8 w-8 p-0"
          aria-label="Previous day"
          data-testid="day-prev"
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={onNext}
          className="h-8 w-8 p-0"
          aria-label="Next day"
          data-testid="day-next"
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={onToday}
          className="h-8 px-3 border-[#D9E1E3] text-[#2E3A3F]"
          data-testid="day-today-button"
        >
          Today
        </Button>
        <div className="font-heading text-base font-medium flex items-center gap-2" data-testid="day-label">
          {dayLabel}
          {showNowPill && (
            <button
              type="button"
              onClick={handleJumpToNow}
              data-testid="day-jump-to-now"
              aria-label={`Jump to now (${nowLabel})`}
              className="inline-flex items-center gap-1 h-6 px-2 rounded-full bg-[#1F9FA4] text-white text-[11px] font-medium hover:bg-[#116B6E] active:bg-[#0B4F52] transition-colors focus:outline-none focus:ring-2 focus:ring-[#1F9FA4]/60"
            >
              <Clock className="w-3 h-3" strokeWidth={2.2} aria-hidden="true" />
              <span className="font-mono-data">Now · {nowLabel}</span>
            </button>
          )}
        </div>
      </div>

      {/* Out-of-window events rail — anything before 06:00 or after
          22:00 shows here so we don't drop it. Most users won't see
          this most days. */}
      {outOfWindow.length > 0 && (
        <div className="px-3 py-2 border-b border-[#D9E1E3] bg-[#FAFBFB]" data-testid="day-out-of-window-rail">
          <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A] mb-1.5">
            Outside 06:00–22:00
          </div>
          <ul className="space-y-1">
            {outOfWindow.map((ev) => (
              <li key={ev.id}>
                <button
                  type="button"
                  onClick={() => onEventClick?.(ev)}
                  className={`w-full text-left px-2 py-1.5 border rounded-sm text-xs flex items-center gap-2 ${typeClass(ev.type, parentIdx(ev.parent_id))}`}
                  data-testid={`day-out-of-window-event-${ev.id}`}
                >
                  <span className="font-mono-data text-[10px] tabular-nums">
                    {String(Math.floor(ev._startMin / 60)).padStart(2, "0")}:
                    {String(ev._startMin % 60).padStart(2, "0")}
                  </span>
                  <span className="truncate flex-1">{ev.title}</span>
                  {!ev.is_shared ? (
                    <EyeOff className="w-3 h-3 flex-shrink-0" aria-label="Private" />
                  ) : (
                    <Eye className="w-3 h-3 flex-shrink-0 opacity-60" aria-label="Shared" />
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Time grid. Single column — events stack vertically with
          absolute positioning relative to the hour band. */}
      <div className="flex">
        {/* Time-of-day rail */}
        <div className="w-12 flex-shrink-0 border-r border-[#D9E1E3] bg-[#FAFBFB]">
          {HOURS.map((h) => (
            <div
              key={h}
              className="h-14 px-1.5 text-[10px] text-[#6F828A] font-mono-data tabular-nums text-right border-b border-[#EEF3F4] flex items-start justify-end pt-0.5"
            >
              {fmtHour(h)}
            </div>
          ))}
        </div>

        {/* Event area */}
        <div
          className="flex-1 relative"
          style={{ height: `${HOURS.length * 56}px` }}
          onClick={onGridClick}
          data-testid="day-event-area"
        >
          {/* Hour separator lines */}
          {HOURS.map((h, i) => (
            <div
              key={h}
              className="absolute left-0 right-0 border-b border-[#EEF3F4]"
              style={{ top: `${(i / HOURS.length) * 100}%`, height: `${100 / HOURS.length}%` }}
              aria-hidden="true"
            />
          ))}

          {/* Live "now" line — only when today + in-window. */}
          {showNowPill && nowTopPct !== null && (
            <div
              ref={nowLineRef}
              data-testid="day-now-line"
              aria-label={`Current time ${nowLabel}`}
              className="absolute left-0 right-0 z-20 pointer-events-none"
              style={{ top: `${nowTopPct}%` }}
            >
              <div className="relative h-0">
                <div className="absolute -left-1 -top-1 w-2 h-2 rounded-full bg-[#D14343]" />
                <div className="absolute left-1 right-0 top-0 h-px bg-[#D14343]" />
              </div>
            </div>
          )}

          {/* Event chips. */}
          {inWindow.map((ev) => {
            const s = new Date(ev.start);
            const e = ev.end ? new Date(ev.end) : new Date(s.getTime() + 30 * 60 * 1000);
            const startMin = Math.max(WINDOW_START_MIN, minSinceMidnight(s));
            const endMin = Math.min(WINDOW_END_MIN, minSinceMidnight(e));
            const topPct = ((startMin - WINDOW_START_MIN) / TOTAL_MIN) * 100;
            const heightPct = Math.max(
              ((endMin - startMin) / TOTAL_MIN) * 100,
              (15 / TOTAL_MIN) * 100
            );
            return (
              <button
                key={ev.id}
                type="button"
                onClick={(e2) => {
                  e2.stopPropagation();
                  onEventClick?.(ev);
                }}
                className={`absolute left-1 right-1 rounded-sm border px-2 py-1 text-left text-xs overflow-hidden hover:shadow-md transition-shadow ${typeClass(ev.type, parentIdx(ev.parent_id))} ${ev.linked_journal_count > 0 ? "border-l-[3px] border-l-[#116B6E]" : ""}`}
                style={{ top: `${topPct}%`, height: `${heightPct}%`, minHeight: "20px" }}
                data-testid={`day-event-${ev.id}`}
                title={`${TYPE_LABEL[ev.type] || ev.type}: ${ev.title}`}
              >
                <div className="flex items-center gap-1 min-w-0">
                  {ev.recurrence && (
                    <Repeat className="w-3 h-3 flex-shrink-0 opacity-70" strokeWidth={1.8} aria-label="Recurring" />
                  )}
                  {!ev.is_shared ? (
                    <EyeOff className="w-3 h-3 flex-shrink-0" aria-label="Private" />
                  ) : (
                    <Eye className="w-3 h-3 flex-shrink-0 opacity-50" aria-label="Shared" />
                  )}
                  {ev.linked_journal_count > 0 && (
                    <span
                      className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-sm bg-[#116B6E] text-white flex-shrink-0"
                      aria-label={`${ev.linked_journal_count} linked journal note${ev.linked_journal_count === 1 ? "" : "s"}`}
                    >
                      <BookOpen className="w-2.5 h-2.5" strokeWidth={2.2} />
                    </span>
                  )}
                  <span className="font-mono-data text-[10px] tabular-nums opacity-80 flex-shrink-0">
                    {String(s.getHours()).padStart(2, "0")}:
                    {String(s.getMinutes()).padStart(2, "0")}
                  </span>
                  <span className="truncate flex-1 font-medium">{ev.title}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Empty state */}
      {inWindow.length === 0 && outOfWindow.length === 0 && (
        <div className="px-3 py-4 text-center text-sm text-[#6F828A]" data-testid="day-empty">
          No events on this day. Tap any time slot to add one.
        </div>
      )}
    </div>
  );
}

export default DayView;
